// roleScaffold.js — compose a prescribed Thinker→Worker runTask decorator (role-scaffold-twv E1-WS2-T01).
// @arch:pure-engine
//
// FORM only. Sequences the THINKER (a reasoning pre-pass) then the WORKER (the existing per-task agent)
// into ONE runTask(task) compatible with taskExecutor.runTaskGraph. The VERIFIER is NOT here — it stays the
// deterministic oracle injected as taskExecutor.verifyTask (A LEI: the oracle is the sole judge, no model).
//
// PURE over injected seams (doctrine [T1] arch:lib-purity): assign/think/work are injected; no react, no
// window, no fetch. We DECORATE the executor's runTask seam — taskExecutor.js itself is never edited.

const isAborted = (signal) => !!(signal && signal.aborted);

// Fase do THINKER (extraída do runTask pelo ratchet de complexidade): devolve a nota
// limpa ou null; qualquer falha/vazio degrada pra Worker-only com o evento de skip.
async function runThinkerPhase({ thinker, think, task, effSignal, runCtx, emit }) {
  if (!thinker) return null;
  if (typeof think !== 'function') { emit('role_thinker_skipped', { taskId: task?.id, reason: 'no_think_fn' }); return null; }
  try {
    const note = await think({ task, model: thinker, signal: effSignal, runCtx });
    const clean = note == null ? '' : String(note).trim();
    if (clean) { emit('role_thinker_done', { taskId: task?.id, model: thinker }); return clean; }
    emit('role_thinker_skipped', { taskId: task?.id, reason: 'empty' });
  } catch (e) {
    emit('role_thinker_skipped', { taskId: task?.id, reason: e?.message || 'error' });
  }
  return null;
}

/**
 * Build a runTask(task) that runs Thinker → Worker. The Worker receives the task augmented with a
 * `thinkerNote` ONLY when the Thinker produced one — so the no-note shape is byte-identical to calling
 * the Worker directly (the OFF path). Thinker failure/empty degrades gracefully to Worker-only.
 *
 * @param {{
 *   assign: object | ((task:object)=>object),     // {thinker,worker,verifier} or a per-task factory
 *   think?: (a:{task:object,model:string,signal?:AbortSignal,runCtx?:object})=>Promise<string|null>|string|null,
 *   work: (task:object, runCtx?:object)=>Promise<any>|any, // the Worker (existing runLocalAgentLoop closure)
 *   onEvent?: (evt:object)=>void,
 *   signal?: AbortSignal,
 * }} cfg
 * @returns {(task:object, runCtx?:object)=>Promise<any>} runTask
 */
export function composeRoleScaffold(cfg) {
  const { assign, think, work, onEvent = () => {}, signal } = cfg || {};
  if (typeof work !== 'function') throw new Error('composeRoleScaffold: work is required');
  const emit = (type, extra) => { try { onEvent({ type, ...extra }); } catch { /* sink swallow */ } };

  // fix H/B — runCtx (signal por-task do deadline / parallel da wave) atravessa o
  // scaffold intacto até o Worker E até o Thinker: o signal efetivo é o do deadline
  // por task quando presente (senão o do turno — assinatura antiga, byte-idêntico),
  // então o teto do fix H corta TAMBÉM a fase de thinker (review adversarial).
  return async function runTask(task, runCtx) {
    const effSignal = runCtx?.signal || signal;
    const roles = typeof assign === 'function' ? (assign(task) || {}) : (assign || {});
    const thinker = roles.thinker ? String(roles.thinker) : null;
    const worker = roles.worker ? String(roles.worker) : null;
    emit('role_assigned', { taskId: task?.id, thinker, worker, verifier: roles.verifier || 'oracle' });

    // Abort surfaced honestly — never a false success.
    if (isAborted(effSignal)) { emit('role_aborted', { taskId: task?.id }); throw new Error('aborted'); }

    const thinkerNote = await runThinkerPhase({ thinker, think, task, effSignal, runCtx, emit });

    if (isAborted(effSignal)) { emit('role_aborted', { taskId: task?.id }); throw new Error('aborted'); }

    // Worker gets the note ONLY when present, so thinker-absent === the exact task the Worker sees today.
    const workerTask = thinkerNote ? { ...task, thinkerNote } : task;
    emit('role_worker_start', { taskId: task?.id, model: worker });
    const result = await work(workerTask, runCtx);
    emit('role_worker_done', { taskId: task?.id, model: worker });
    return result;
  };
}
